import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SocialPlatform } from "@digital-twins/shared-types";
import { cn } from "@/lib/utils";
import { SocialBarList, SocialBarListRow } from "./charts/SocialBarList";
import { measureOf, sharesLargestRemainder } from "./charts/socialTopicMetrics";
import { SOCIAL_PANEL_BOTTOM_PADDING, SocialTopicPanelHeader } from "./SocialTopicPanelHeader";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { useTopicHighlight } from "./SocialTopicHighlightContext";
import { formatSocialCount, SOCIAL_PLATFORM_LABELS } from "./socialFormat";
import { useSocialUniverseCounts } from "./useSocialUniverse";

interface SocialTopicChannelsProps {
  /** Reads the channel counts; a row click sets the channel filter. */
  context: SocialTopicSlotContext;
  className?: string;
}

const PLATFORM_LABEL = (key: string) => SOCIAL_PLATFORM_LABELS[key as SocialPlatform] ?? key;

/**
 * The channel split in the current measure: reach (views) or posts. A channel
 * whose posts report no views says "No views reported" in reach mode instead
 * of a bar at zero. The channel filter is left out of its own counts, so the
 * other channels stay on screen.
 */
export function SocialTopicChannels(props: SocialTopicChannelsProps) {
  const { context, className } = props;
  const { universe, topicFilters, refreshKey } = context;

  const { t, i18n } = useTranslation("app");
  const { setActive } = useTopicHighlight();
  const { metric, platform: selectedPlatform } = topicFilters.filters;
  const filters = useMemo(() => topicFilters.facetFilters("channel"), [topicFilters]);
  const { counts } = useSocialUniverseCounts(
    universe.token,
    { groupBy: "channel", filters, includeMarket: true },
    refreshKey,
  );

  const rows = useMemo((): Array<SocialBarListRow> => {
    if (!counts) {
      return [];
    }
    const channelKeys = new Set<string>(universe.channels.map((channel) => channel.platform));
    const entries = counts.cells
      .filter((cell) => channelKeys.has(cell.key))
      .map((cell) => ({
        cell,
        hasNoValue: metric === "reach" && (cell.viewsPosts ?? 0) <= 0,
        value: measureOf(metric, cell.count, cell.views),
      }))
      .sort(
        (a, b) =>
          b.value - a.value ||
          b.cell.count - a.cell.count ||
          PLATFORM_LABEL(a.cell.key).localeCompare(PLATFORM_LABEL(b.cell.key)),
      );
    const shares = sharesLargestRemainder(entries.map((entry) => entry.value));
    const max = Math.max(1, ...entries.map((entry) => entry.value));
    return entries.map((entry, index) => {
      const { cell, hasNoValue, value } = entry;
      return {
        key: cell.key,
        label: PLATFORM_LABEL(cell.key),
        count: cell.count,
        share: hasNoValue ? null : (value * 100) / max,
        valueLabel: hasNoValue
          ? t("socialListening.topicPage.channels.posts", { count: cell.count })
          : formatSocialCount(value, i18n.language),
        detailLabel: hasNoValue
          ? undefined
          : t("socialListening.topicKpis.percent", { value: shares?.[index] ?? 0 }),
        platform: cell.key as SocialPlatform,
        isSelected: cell.key === selectedPlatform,
        isDimmed: selectedPlatform != null && selectedPlatform !== cell.key,
        hasNoValue,
      };
    });
  }, [counts, universe.channels, metric, selectedPlatform, t, i18n.language]);

  const hint = useMemo(() => {
    if (!counts) {
      return null;
    }
    if (metric !== "reach") {
      return t("socialListening.topicPage.channels.byPosts");
    }
    const channelKeys = new Set<string>(universe.channels.map((channel) => channel.platform));
    const own = counts.cells.filter((cell) => channelKeys.has(cell.key));
    return t("socialListening.topicPage.stats.reachCoverage", {
      covered: own.reduce((sum, cell) => sum + (cell.viewsPosts ?? 0), 0),
      count: own.reduce((sum, cell) => sum + cell.count, 0),
    });
  }, [counts, metric, universe.channels, t]);

  const onSelect = (key: string) => {
    topicFilters.setFilter("platform", key === selectedPlatform ? null : (key as SocialPlatform));
  };

  return (
    <section
      aria-label={t("socialListening.topicPage.channels.title")}
      className={cn(SOCIAL_PANEL_BOTTOM_PADDING, className)}
      data-testid="social-topic-channels"
    >
      <SocialTopicPanelHeader
        title={t("socialListening.topicPage.channels.title")}
        trailing={hint}
      />
      <div className="px-3 pt-2">
        {counts ? (
          <SocialBarList
            rows={rows}
            onSelect={onSelect}
            onHighlight={(key) =>
              setActive(key ? { kind: "channel", platform: key as SocialPlatform } : null)
            }
            testIdPrefix="social-topic-channel-bar"
            ariaLabel={t("socialListening.topicPage.channels.title")}
            noValueLabel={t("socialListening.topicPage.channels.noViews")}
          />
        ) : null}
      </div>
    </section>
  );
}
